import { getCountryData } from '../constants/countries';

/**
 * Formats a price with the appropriate currency symbol based on country
 * @param {number|string} amount - The amount to format
 * @param {string} countryCode - The country code (e.g. MY, SG, PH, ID)
 * @returns {string} Formatted price with currency symbol
 */
export const formatPrice = (amount, countryCode = 'MY') => {
  const countryData = getCountryData(countryCode);
  const currencySymbol = countryData.currencySymbol;
  
  // Ensure amount is a number
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle invalid amounts
  if (isNaN(numAmount)) {
    return `${currencySymbol}0`;
  }
  
  // Format based on country
  switch (countryCode) {
    case 'PH':
      // Philippines uses comma as thousands separator
      return `${currencySymbol}${numAmount.toLocaleString('en-PH')}`;
    case 'SG':
      // Singapore uses comma as thousands separator
      return `${currencySymbol}${numAmount.toLocaleString('en-SG')}`;
    case 'ID':
      // Indonesia uses dot as thousands separator, space after symbol
      return `${currencySymbol} ${numAmount.toLocaleString('id-ID')}`;
    case 'VN':
      // Vietnam uses dot as thousands separator, space after symbol
      return `${currencySymbol} ${numAmount.toLocaleString('vi-VN')}`;
    case 'IN':
      // India groups digits in lakhs and crores (1,00,000)
      return `${currencySymbol}${numAmount.toLocaleString('en-IN')}`;
    case 'USD':
      // USD uses comma as thousands separator
      return `${currencySymbol}${numAmount.toLocaleString('en-US')}`;
    case 'MY':
    default:
      // Malaysia uses comma as thousands separator
      return `${currencySymbol}${numAmount.toLocaleString('en-MY')}`;
  }
};

/**
 * Formats a price range with the appropriate currency symbol
 * @param {number|string} minAmount - The minimum amount
 * @param {number|string} maxAmount - The maximum amount
 * @param {string} countryCode - The country code (e.g. MY, SG, PH, ID)
 * @returns {string} Formatted price range
 */
export const formatPriceRange = (minAmount, maxAmount, countryCode = 'MY') => {
  const countryData = getCountryData(countryCode);
  const currencySymbol = countryData.currencySymbol;
  
  // Ensure amounts are numbers
  const numMinAmount = typeof minAmount === 'string' ? parseFloat(minAmount) : minAmount;
  const numMaxAmount = typeof maxAmount === 'string' ? parseFloat(maxAmount) : maxAmount;
  
  // Handle invalid amounts
  if (isNaN(numMinAmount) || isNaN(numMaxAmount)) {
    return `${currencySymbol}0 - ${currencySymbol}0`;
  }
  
  // Format based on country
  switch (countryCode) {
    case 'PH':
      return `${currencySymbol}${numMinAmount.toLocaleString('en-PH')} - ${currencySymbol}${numMaxAmount.toLocaleString('en-PH')}`;
    case 'SG':
      return `${currencySymbol}${numMinAmount.toLocaleString('en-SG')} - ${currencySymbol}${numMaxAmount.toLocaleString('en-SG')}`;
    case 'ID':
      return `${currencySymbol} ${numMinAmount.toLocaleString('id-ID')} - ${currencySymbol} ${numMaxAmount.toLocaleString('id-ID')}`;
    case 'VN':
      return `${currencySymbol} ${numMinAmount.toLocaleString('vi-VN')} - ${currencySymbol} ${numMaxAmount.toLocaleString('vi-VN')}`;
    case 'IN':
      return `${currencySymbol}${numMinAmount.toLocaleString('en-IN')} - ${currencySymbol}${numMaxAmount.toLocaleString('en-IN')}`;
    case 'USD':
      return `${currencySymbol}${numMinAmount.toLocaleString('en-US')} - ${currencySymbol}${numMaxAmount.toLocaleString('en-US')}`;
    case 'MY':
    default:
      return `${currencySymbol}${numMinAmount.toLocaleString('en-MY')} - ${currencySymbol}${numMaxAmount.toLocaleString('en-MY')}`;
  }
};

/**
 * Gets the currency symbol for a country
 * @param {string} countryCode - The country code (e.g. MY, SG, PH, ID)
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (countryCode = 'MY') => {
  const countryData = getCountryData(countryCode);
  return countryData.currencySymbol;
};

/**
 * Resolves the currency a job's prices should be displayed in.
 *
 * Job prices are stored in the job's home-country currency with no FX
 * conversion, so the job's own country must decide the symbol — never the
 * viewer's country, or a PH user would see an RM3,000 salary labeled ₱3,000.
 * International postings (type 4 / `international` target country set) are
 * priced in USD.
 *
 * @param {Object} [job] - Job-like object with country/type/international
 * @param {string} [fallbackCountry] - Country code used only when the job has
 *   no country (legacy rows), e.g. the viewer's country or currency preset
 * @returns {string} Country/currency code for formatPrice & formatPriceRange
 */
export const getJobDisplayCurrency = (job, fallbackCountry) => {
  const isInternational = job?.type === 4 || job?.type === '4' || !!job?.international;
  if (isInternational) return 'USD';
  return job?.country || fallbackCountry || 'MY';
};

/**
 * Gets the max number of digits a price input should accept for a country
 * @param {string} countryCode - The country code (e.g. MY, VN)
 * @returns {number} Max input length
 */
export const getPriceMaxLength = (countryCode = 'MY') => {
  return getCountryData(countryCode).priceMaxLength || 4;
};
